// 메일 본문 서식 편집기.
//
// 왜 라이브러리를 안 쓰는가:
//   메일 HTML 은 **단순해야** 받는 쪽에서 제대로 보인다. 아웃룩·네이버·지메일은
//   각자 다르게 렌더하고 <style> 블록이나 최신 CSS 를 잘라 낸다. 전문 편집기는
//   화면에선 예쁘지만 메일로 보내면 깨지는 마크업을 만들기 쉽다.
//   브라우저 내장 편집 기능(contentEditable + execCommand)으로 만들면
//   b/i/u·font·ol/ul·table 같은 **20년 넘게 모든 메일 앱이 이해하는 태그**만 나온다.
//
// execCommand 는 표준에서 폐기 예정으로 표시돼 있지만, 대체 표준이 아직 없고
// 모든 브라우저가 계속 지원한다. 메일 편집기라는 좁은 용도에서는 지금도 가장
// 안전한 선택이라 판단했다.
//
// 표 편집(행·열 추가/삭제)만은 execCommand 에 해당 명령이 없어 DOM 을 직접 만진다.

import { useCallback, useEffect, useRef, useState } from "react";

export type RichEditorHandle = {
  /** 현재 내용을 HTML 로 */
  getHtml: () => string;
  /** 현재 내용을 평문으로(줄바꿈 유지) */
  getText: () => string;
  /** 커서 위치에 HTML 삽입 */
  insertHtml: (html: string) => void;
  /** 내용을 통째로 갈아끼운다(AI 첨삭 적용처럼 바깥이 글을 새로 써 줄 때).
   *  initialHtml 은 "비어 있을 때만" 들어가므로 이미 쓴 글은 그것으로 못 바꾼다. */
  setHtml: (html: string) => void;
  /** 이 노드가 편집기 본문 안에 있는가(드롭이 본문에 떨어졌는지 가릴 때). */
  containsNode: (n: Node | null) => boolean;
  /** 화면 좌표 자리에 커서를 둔다(끌어다 놓은 그 자리에 그림을 넣으려고). */
  placeCaretAt: (x: number, y: number) => void;
};

const CELL_STYLE = "border:1px solid #d1d5db;padding:6px 8px;min-width:60px";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** 엑셀·구글시트에서 복사한 HTML 을 **깨끗한 표 하나**로 바꾼다.
 *
 *  엑셀이 클립보드에 주는 HTML 은 mso 스타일 블록·xml 네임스페이스·클래스가 잔뜩 붙어 있어
 *  그대로 넣으면 메일 앱마다 다르게 깨진다. 그래서 셀의 글자와 병합(colspan/rowspan),
 *  굵게·정렬·배경색·글자색만 골라 우리 편집기의 표 모양으로 다시 짓는다.
 *  표가 없으면 null (호출 쪽이 평문으로 넣는다). */
function tableFromHtml(html: string): string | null {
  if (!/<table[\s>]/i.test(html)) return null;
  let doc: Document;
  try { doc = new DOMParser().parseFromString(html, "text/html"); } catch { return null; }
  const table = doc.querySelector("table");
  if (!table) return null;
  const rows = Array.from(table.querySelectorAll("tr")).filter((tr) => tr.closest("table") === table);
  if (!rows.length) return null;

  // 엑셀은 서식을 거의 다 <style> 블록의 클래스(.xl65, .font5 …)와 td 기본 규칙으로 준다.
  // 인라인 style 만 보면 색·글꼴이 전부 빠진다(2026-09-14 "셀 색상이랑 폰트도 가져오게").
  // 선택자 → 선언 문자열로 모아 두고, 요소마다 [태그 규칙 → 클래스 규칙 → 인라인] 순으로 합친다.
  const rules = new Map<string, string>();
  doc.querySelectorAll("style").forEach((st) => {
    const css = (st.textContent || "").replace(/<!--|-->/g, "");
    for (const m of css.matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
      const decl = m[2].trim();
      for (const sel of m[1].split(",")) {
        const key = sel.trim().toLowerCase();
        if (key) rules.set(key, (rules.get(key) ? rules.get(key) + ";" : "") + decl);
      }
    }
  });
  const styleOf = (el: Element): string => {
    const tag = el.tagName.toLowerCase();
    const parts: string[] = [];
    if (rules.has(tag)) parts.push(rules.get(tag)!);
    for (const c of Array.from(el.classList)) { const r = rules.get("." + c.toLowerCase()); if (r) parts.push(r); }
    const fc = el.getAttribute("color"); if (fc) parts.push(`color:${fc}`);          // <font color>
    const ff = el.getAttribute("face");  if (ff) parts.push(`font-family:${ff}`);    // <font face>
    const st = el.getAttribute("style"); if (st) parts.push(st);
    if (tag === "b" || tag === "strong") parts.push("font-weight:bold");
    if (tag === "i" || tag === "em") parts.push("font-style:italic");
    if (tag === "u") parts.push("text-decoration:underline");
    if (tag === "s" || tag === "strike") parts.push("text-decoration:line-through");
    return parts.join(";");
  };
  // 합쳐진 선언에서 속성 하나를 뽑는다 — 뒤에 나온 선언이 이긴다.
  const prop = (decl: string, name: string): string | undefined => {
    const re = new RegExp("(?:^|;)\\s*" + name + "\\s*:\\s*([^;]+)", "gi");
    let last: string | undefined; let m: RegExpExecArray | null;
    while ((m = re.exec(decl))) last = m[1].trim();
    return last;
  };
  const safeColor = (v?: string): string => {
    const x = (v || "").trim();
    return /^(#[0-9a-f]{3,8}|rgb\([\d\s,.%]+\)|[a-z]+)$/i.test(x) ? x : "";
  };
  const safeFont = (v?: string): string => {
    // 큰따옴표("맑은 고딕")는 style="…" 속성 안에서 속성을 끊어 버리므로 작은따옴표로 바꾼다.
    const x = (v || "").trim().replace(/\s*!important/i, "").replace(/"/g, "'");
    return x && /^[\w\s,'\-\u3131-\uD79D]+$/.test(x) && x.length < 80 ? x : "";
  };
  const safeSize = (v?: string): string => {
    const m = (v || "").trim().match(/^(\d+(?:\.\d+)?)(pt|px)$/i);
    if (!m) return "";
    const n = parseFloat(m[1]);
    return n >= 6 && n <= 72 ? `${Math.round(n * 10) / 10}${m[2].toLowerCase()}` : "";
  };

  // 글자 요소(font/span/b …) 하나의 글자 서식만 뽑는다(셀 배경·정렬은 제외).
  const textStyle = (el: Element): string => {
    const decl = styleOf(el);
    const out: string[] = [];
    if (/^(bold|bolder|[6-9]00)$/i.test(prop(decl, "font-weight") || "")) out.push("font-weight:bold");
    if (/^(italic|oblique)/i.test(prop(decl, "font-style") || "")) out.push("font-style:italic");
    const deco = prop(decl, "text-decoration") || "";
    if (/underline/i.test(deco)) out.push("text-decoration:underline");
    else if (/line-through/i.test(deco)) out.push("text-decoration:line-through");
    const color = safeColor(prop(decl, "color"));
    if (color && !/^(windowtext)$/i.test(color)) out.push(`color:${color}`);
    const fam = safeFont(prop(decl, "font-family")); if (fam) out.push(`font-family:${fam}`);
    const size = safeSize(prop(decl, "font-size")); if (size) out.push(`font-size:${size}`);
    return out.join(";");
  };
  // 셀 내용: 글자는 이스케이프, <br> 은 줄바꿈, 부분 서식 요소는 <span style> 로 남긴다.
  // 엑셀은 한 셀 안에서 일부 글자만 색·글꼴이 다를 때 <font class=…> 로 준다.
  const renderInline = (node: Node): string => {
    if (node.nodeType === 3) return escapeHtml((node.textContent || "").replace(/\u00a0/g, " "));
    if (node.nodeType !== 1) return "";
    const el = node as Element;
    if (el.tagName === "BR") return "<br>";
    if (el.tagName === "IMG" || el.tagName === "SCRIPT" || el.tagName === "STYLE") return "";
    const inner = Array.from(el.childNodes).map(renderInline).join("");
    if (!inner.trim()) return inner;
    const st = /^(FONT|SPAN|B|STRONG|I|EM|U|S|STRIKE|A)$/.test(el.tagName) ? textStyle(el) : "";
    return st ? `<span style="${st}">${inner}</span>` : inner;
  };
  const cellHtml = (cell: HTMLTableCellElement): string => {
    const html = Array.from(cell.childNodes).map(renderInline).join("")
      .replace(/^(\s|<br>)+|(\s|<br>)+$/g, "");   // 앞뒤 공백·줄바꿈 정리
    return html.replace(/<[^>]+>/g, "").trim() ? html : "&nbsp;";
  };
  const cellStyle = (cell: HTMLTableCellElement): string => {
    // 셀 자신의 서식(td 기본 규칙 + 클래스 + 인라인). 안쪽 글자 요소의 서식은 cellHtml 이 span 으로 살린다.
    const own = styleOf(cell);
    const decl = own;
    const out: string[] = [CELL_STYLE];
    if (cell.tagName === "TH" || /^(bold|bolder|[6-9]00)$/i.test(prop(decl, "font-weight") || "")) out.push("font-weight:bold");
    if (/^(italic|oblique)/i.test(prop(decl, "font-style") || "")) out.push("font-style:italic");
    const deco = prop(decl, "text-decoration") || "";
    if (/underline/i.test(deco)) out.push("text-decoration:underline");
    else if (/line-through/i.test(deco)) out.push("text-decoration:line-through");
    const align = (cell.getAttribute("align") || prop(decl, "text-align") || "").toLowerCase();
    if (/^(center|right)$/.test(align)) out.push(`text-align:${align}`);
    const valign = (cell.getAttribute("valign") || prop(decl, "vertical-align") || "").toLowerCase();
    if (/^(top|bottom)$/.test(valign)) out.push(`vertical-align:${valign}`);
    const color = safeColor(prop(decl, "color"));
    if (color && !/^(#000(000)?|black|windowtext)$/i.test(color)) out.push(`color:${color}`);
    // 배경은 셀 자신의 것만 본다(글자 요소의 배경은 강조 표시라 셀 칠하기와 다르다).
    const bgRaw = prop(own, "background-color") || prop(own, "background") || cell.getAttribute("bgcolor") || "";
    const bg = safeColor(bgRaw.split(/\s+/)[0]);
    if (bg && !/^(#fff(fff)?|white|transparent|none)$/i.test(bg)) out.push(`background:${bg}`);
    const fam = safeFont(prop(decl, "font-family"));
    if (fam) out.push(`font-family:${fam}`);
    const size = safeSize(prop(decl, "font-size"));
    if (size) out.push(`font-size:${size}`);
    // 열 너비(px 또는 pt)는 첫 셀에 남겨 두면 비율이 유지된다.
    const w = (cell.getAttribute("width") || prop(own, "width") || "").trim();
    const wm = w.match(/^(\d+(?:\.\d+)?)(px|pt)?$/i);
    if (wm) out.push(`width:${Math.round(parseFloat(wm[1]) * (wm[2]?.toLowerCase() === "pt" ? 4 / 3 : 1))}px`);
    return out.join(";");
  };

  let out = '<table style="border-collapse:collapse;margin:8px 0" border="1"><tbody>';
  let cells = 0;
  for (const tr of rows) {
    const tds = Array.from(tr.children).filter((c): c is HTMLTableCellElement => c.tagName === "TD" || c.tagName === "TH");
    if (!tds.length) continue;
    out += "<tr>";
    for (const td of tds) {
      const span: string[] = [];
      const cs = parseInt(td.getAttribute("colspan") || "1", 10), rs = parseInt(td.getAttribute("rowspan") || "1", 10);
      if (cs > 1) span.push(`colspan="${cs}"`);
      if (rs > 1) span.push(`rowspan="${rs}"`);
      const tag = td.tagName === "TH" ? "th" : "td";
      out += `<${tag} ${span.join(" ")} style="${cellStyle(td)}">${cellHtml(td)}</${tag}>`;
      cells++;
    }
    out += "</tr>";
  }
  out += "</tbody></table><p><br></p>";
  return cells ? out : null;
}

/** 탭으로 나뉜 여러 줄(엑셀이 HTML 없이 평문만 줄 때, 또는 메모장에서 복사한 TSV)을 표로. */
function tableFromTsv(text: string): string | null {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length < 2 || !lines.every((l) => l.includes("\t"))) return null;
  const width = Math.max(...lines.map((l) => l.split("\t").length));
  if (width < 2) return null;
  let out = '<table style="border-collapse:collapse;margin:8px 0" border="1"><tbody>';
  for (const l of lines) {
    const cols = l.split("\t");
    while (cols.length < width) cols.push("");
    out += "<tr>" + cols.map((c) => `<td style="${CELL_STYLE}">${c.trim() ? escapeHtml(c.trim()) : "&nbsp;"}</td>`).join("") + "</tr>";
  }
  return out + "</tbody></table><p><br></p>";
}

const FONT_SIZES = [
  { label: "아주 작게", v: "1" },
  { label: "작게", v: "2" },
  { label: "보통", v: "3" },
  { label: "조금 크게", v: "4" },
  { label: "크게", v: "5" },
  { label: "아주 크게", v: "6" },
];

// 받는 쪽에 그 글꼴이 없으면 대체 글꼴로 그려진다. 그래서 **뒤에 계열을 붙여**
// 최소한 비슷한 모양으로는 보이게 한다(한글 글꼴은 sans-serif/serif 로 갈린다).
const FONTS = [
  { label: "기본", v: "" },
  { label: "맑은 고딕", v: "'Malgun Gothic', sans-serif" },
  { label: "돋움", v: "Dotum, sans-serif" },
  { label: "굴림", v: "Gulim, sans-serif" },
  { label: "바탕", v: "Batang, serif" },
  { label: "나눔고딕", v: "'Nanum Gothic', sans-serif" },
  { label: "Arial", v: "Arial, sans-serif" },
  { label: "Georgia", v: "Georgia, serif" },
  { label: "Courier", v: "'Courier New', monospace" },
];

const BLOCKS = [
  { label: "본문", v: "<p>" },
  { label: "제목 1", v: "<h1>" },
  { label: "제목 2", v: "<h2>" },
  { label: "제목 3", v: "<h3>" },
];

const COLORS = [
  "#1f2937", "#374151", "#6b7280", "#9ca3af", "#ffffff",
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
  "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#3b82f6",
  "#6366f1", "#8b5cf6", "#a855f7", "#ec4899", "#f43f5e",
];

const HILITES = [
  "#fef08a", "#fecaca", "#bbf7d0", "#bfdbfe", "#e9d5ff",
  "#fed7aa", "#fbcfe8", "#d9f99d", "#a5f3fc", "transparent",
];

const EMOJIS = [
  "😀","😄","😊","🙂","😉","😍","🤔","😅","😂","🥲",
  "👍","👏","🙏","🙌","💪","✌️","👌","🤝","🫶","👀",
  "✅","❗","❓","⭐","🔥","💡","📌","📎","📅","⏰",
  "📧","📞","🏢","🚀","🎉","🎯","💰","📊","📝","✏️",
];

/** 클립보드에서 이미지 파일만 골라낸다.
 *
 *  화면 캡처를 붙여넣으면 이름 없는 image/png 덩어리로 온다. 그대로 두면
 *  이름이 빈 파일이 되어 서버에서 거절당하므로 여기서 이름을 지어 준다. */
const IMAGE_EXT: Record<string, string> = {
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
  bmp: "image/bmp", svg: "image/svg+xml", heic: "image/heic", avif: "image/avif", tif: "image/tiff", tiff: "image/tiff",
};

/** 이미지 파일인가. MIME 이 비어 있으면 확장자로 본다.
 *
 *  탐색기·메신저에서 복사한 파일은 브라우저가 종류(type)를 비워 둔 채 넘기는 일이 있어,
 *  type 만 보면 "이미지가 아니다"로 빠져 파일명 글자만 붙는다(2026-09-15 실제 증상). */
export function isImageFile(f: File): boolean {
  if (f.type && f.type.startsWith("image/")) return true;
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  return !f.type && ext in IMAGE_EXT;
}

/** 서버가 종류를 보고 저장하므로, 비어 있으면 확장자로 채워 준다. 이름 없는 캡처는 이름도 지어 준다. */
export function asImageFile(f: File): File {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  const type = f.type && f.type.startsWith("image/") ? f.type : (IMAGE_EXT[ext] || "image/png");
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, "");
  const name = f.name && f.name !== "image.png" ? f.name : `캡처-${stamp}.${type.split("/")[1]?.replace("jpeg", "jpg") || "png"}`;
  if (name === f.name && type === f.type) return f;
  return new File([f], name, { type });
}

function imagesFromClipboard(dt: DataTransfer): File[] {
  const out: File[] = [];
  const seen = new Set<File>();
  // items 와 files 를 모두 본다 — 붙여넣기는 items, 끌어놓기는 files 쪽이 더 믿을 만하다.
  for (const item of Array.from(dt.items || [])) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (f && !seen.has(f) && isImageFile(f)) { seen.add(f); out.push(asImageFile(f)); }
  }
  if (!out.length) {
    for (const f of Array.from(dt.files || [])) {
      if (!seen.has(f) && isImageFile(f)) { seen.add(f); out.push(asImageFile(f)); }
    }
  }
  return out;
}

export function RichEditor({
  initialHtml,
  onChange,
  onRequestImage,
  onPasteImages,
  editorRef,
  minHeight = 260,
}: {
  initialHtml?: string;
  onChange?: (html: string) => void;
  /** 이미지 버튼을 눌렀을 때. 파일 업로드는 바깥(첨부 로직)이 담당한다. */
  onRequestImage?: () => void;
  /** 이미지를 붙여넣거나 끌어다 놨을 때. 올리고 넣는 일은 바깥이 한다(버튼과 같은 경로). */
  onPasteImages?: (files: File[]) => void;
  editorRef?: React.MutableRefObject<RichEditorHandle | null>;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<null | "color" | "hilite" | "table" | "emoji">(null);
  const [count, setCount] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [marks, setMarks] = useState<Record<string, boolean>>({});
  // 크기를 조절하려고 고른 이미지. 메일에 넣은 사진은 대개 화면 폭보다 커서
  // 그대로 두면 받는 쪽에서 본문이 사진 하나로 꽉 찬다.
  const [selImg, setSelImg] = useState<HTMLImageElement | null>(null);
  // 커서가 표 안에 있으면 표 메뉴가 '만들기' 대신 '편집' 도구를 보여 준다.
  const [inTable, setInTable] = useState(false);

  // 내용 넣기는 **비어 있을 때만** 한다.
  //   · 매번 넣으면 타이핑 중 커서가 맨 앞으로 튄다.
  //   · 그렇다고 첫 렌더에만 넣으면, 답장 서명이 조금 늦게 채워지는 경우
  //     (서명은 열린 뒤에 붙는다) 그 내용을 놓친다. 그래서 "아직 비었으면 넣는다".
  useEffect(() => {
    const el = ref.current;
    if (!el || !initialHtml) return;
    if (!el.innerHTML.trim()) {
      el.innerHTML = initialHtml;
      healSeparators(el);
      setCount((el.innerText || "").trim().length);
    }
  }, [initialHtml]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCount((el.innerText || "").trim().length);
    onChange?.(el.innerHTML);
  }, [onChange]);

  const exec = useCallback((cmd: string, value?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, value);
    emit();
  }, [emit]);

  const insertHtml = useCallback((html: string) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  }, [emit]);

  useEffect(() => {
    if (!editorRef) return;
    editorRef.current = {
      getHtml: () => ref.current?.innerHTML ?? "",
      getText: () => {
        const el = ref.current;
        if (!el) return "";
        // 화면에 보이는 그대로의 줄바꿈을 살린다(innerText 가 그 역할을 한다).
        return el.innerText.replace(/ /g, " ");
      },
      insertHtml,
      setHtml: (html: string) => {
        if (!ref.current) return;
        ref.current.innerHTML = html;
        emit();
      },
      containsNode: (n) => !!(n && ref.current?.contains(n)),
      placeCaretAt: (x, y) => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        const d = document as Document & {
          caretRangeFromPoint?: (x: number, y: number) => Range | null;
          caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
        };
        let range: Range | null = null;
        if (d.caretRangeFromPoint) range = d.caretRangeFromPoint(x, y);
        else if (d.caretPositionFromPoint) {
          const p = d.caretPositionFromPoint(x, y);
          if (p) { range = document.createRange(); range.setStart(p.offsetNode, p.offset); range.collapse(true); }
        }
        if (range && el.contains(range.startContainer)) {
          const sel = window.getSelection();
          sel?.removeAllRanges(); sel?.addRange(range);
        }
      },
    };
  }, [editorRef, insertHtml, emit]);

  /** 커서가 놓인 자리의 서식 상태(굵게가 켜져 있나 등)를 도구 모음에 비춘다.
   *  이게 없으면 지금 굵은 글씨를 쓰는 중인지 눌러 보기 전에는 알 수 없다. */
  const refreshMarks = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    const n = sel?.anchorNode;
    const host = n && (n.nodeType === 1 ? (n as HTMLElement) : n.parentElement);
    if (!host || !el.contains(host)) return;
    const q = (c: string) => { try { return document.queryCommandState(c); } catch { return false; } };
    setInTable(!!host.closest("td,th"));
    setMarks({
      bold: q("bold"), italic: q("italic"), underline: q("underline"),
      strikeThrough: q("strikeThrough"),
      insertOrderedList: q("insertOrderedList"), insertUnorderedList: q("insertUnorderedList"),
      justifyCenter: q("justifyCenter"), justifyRight: q("justifyRight"), justifyFull: q("justifyFull"),
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshMarks);
    return () => document.removeEventListener("selectionchange", refreshMarks);
  }, [refreshMarks]);

  // 팝오버는 바깥을 누르면 닫는다.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  // ── 표 편집 ────────────────────────────────────────────────────────────
  // execCommand 에는 행·열을 다루는 명령이 없다. 표를 넣기만 하고 고치지 못하면
  // 결국 지우고 다시 만들게 되므로, 이 부분만 DOM 을 직접 만진다.
  const currentCell = (): HTMLTableCellElement | null => {
    const sel = window.getSelection();
    let n: Node | null = sel?.anchorNode ?? null;
    while (n && n.nodeType !== 1) n = n.parentNode;
    const el = n as HTMLElement | null;
    if (!el || !ref.current?.contains(el)) return null;
    return el.closest("td,th");
  };

  const insertTable = (rows: number, cols: number, header: boolean) => {
    if (!rows || !cols) return;
    let html = '<table style="border-collapse:collapse;width:100%;margin:8px 0" border="1"><tbody>';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) {
        html += header && r === 0
          ? `<th style="${CELL_STYLE};background:#f3f4f6">머리글</th>`
          : `<td style="${CELL_STYLE}">&nbsp;</td>`;
      }
      html += "</tr>";
    }
    html += "</tbody></table><p><br></p>";
    insertHtml(html);
    setMenu(null);
  };

  /** 병합(colspan/rowspan)을 감안한 격자. grid[r][c] 는 그 자리를 차지한 셀, pos 는 셀의 시작 좌표. */
  const buildGrid = (table: HTMLTableElement) => {
    const grid: (HTMLTableCellElement | undefined)[][] = [];
    const pos = new Map<HTMLTableCellElement, { r: number; c: number }>();
    const rows = Array.from(table.rows);
    rows.forEach((row, r) => {
      grid[r] = grid[r] || [];
      let c = 0;
      Array.from(row.cells).forEach((cell) => {
        while (grid[r][c]) c++;
        const cs = Math.max(1, cell.colSpan), rs = Math.max(1, cell.rowSpan);
        pos.set(cell, { r, c });
        for (let i = 0; i < rs; i++) { grid[r + i] = grid[r + i] || []; for (let j = 0; j < cs; j++) grid[r + i][c + j] = cell; }
        c += cs;
      });
    });
    return { grid, pos, rows };
  };
  const newCell = (like: HTMLTableCellElement | undefined, header = false): HTMLTableCellElement => {
    const c = document.createElement(header || like?.tagName === "TH" ? "th" : "td") as HTMLTableCellElement;
    c.setAttribute("style", header || like?.tagName === "TH" ? `${CELL_STYLE};background:#f3f4f6` : CELL_STYLE);
    c.innerHTML = header || like?.tagName === "TH" ? "머리글" : "&nbsp;";
    return c;
  };
  const focusCell = (c: HTMLTableCellElement) => {
    const sel = window.getSelection(); const r = document.createRange();
    r.selectNodeContents(c); r.collapse(false);
    sel?.removeAllRanges(); sel?.addRange(r);
  };

  type TableOp = "rowAfter" | "rowBefore" | "delRow" | "rowUp" | "rowDown"
    | "colAfter" | "colBefore" | "delCol" | "delTable"
    | "mergeRight" | "mergeDown" | "split" | "toggleHeader" | "widthFull" | "widthAuto" | "borderToggle";

  const tableOp = (op: TableOp) => {
    const cell = currentCell();
    if (!cell) return;
    const row = cell.parentElement as HTMLTableRowElement | null;
    const table = cell.closest("table");
    if (!row || !table) return;
    const idx = cell.cellIndex;
    const rows = Array.from(table.rows);

    if (op === "delTable") { table.remove(); emit(); return; }
    if (op === "rowUp" || op === "rowDown") {
      const other = op === "rowUp" ? row.previousElementSibling : row.nextElementSibling;
      if (!other || other.tagName !== "TR") return;
      row.parentElement?.insertBefore(op === "rowUp" ? row : other, op === "rowUp" ? other : row);
      focusCell(cell); emit(); return;
    }
    if (op === "colBefore") {
      const { grid, pos } = buildGrid(table);
      const c0 = pos.get(cell)?.c ?? idx;
      rows.forEach((r, ri) => {
        const at = grid[ri]?.[c0];
        // 이 행에서 그 열 자리를 차지한 셀이 다른 행에서 내려온(rowspan) 셀이면 건너뛴다.
        if (at && pos.get(at)?.r !== ri) return;
        r.insertBefore(newCell(at), at && at.parentElement === r ? at : null);
      });
      emit(); return;
    }
    if (op === "mergeRight" || op === "mergeDown") {
      const { grid, pos } = buildGrid(table);
      const p = pos.get(cell); if (!p) return;
      const other = op === "mergeRight" ? grid[p.r]?.[p.c + Math.max(1, cell.colSpan)] : grid[p.r + Math.max(1, cell.rowSpan)]?.[p.c];
      if (!other || other === cell) return;
      const q = pos.get(other)!;
      // 모서리가 맞아야 직사각형으로 합쳐진다.
      const fits = op === "mergeRight" ? (q.r === p.r && other.rowSpan === cell.rowSpan) : (q.c === p.c && other.colSpan === cell.colSpan);
      if (!fits) return;
      if ((other.textContent || "").replace(/\u00a0/g, "").trim()) {
        cell.innerHTML = (cell.innerHTML.replace(/^(&nbsp;|\s)+$/, "") ? cell.innerHTML + " " : "") + other.innerHTML;
      }
      if (op === "mergeRight") cell.colSpan = Math.max(1, cell.colSpan) + Math.max(1, other.colSpan);
      else cell.rowSpan = Math.max(1, cell.rowSpan) + Math.max(1, other.rowSpan);
      other.remove();
      focusCell(cell); emit(); return;
    }
    if (op === "split") {
      const cs = Math.max(1, cell.colSpan), rs = Math.max(1, cell.rowSpan);
      if (cs === 1 && rs === 1) return;
      const { pos } = buildGrid(table);
      const p = pos.get(cell); if (!p) return;
      cell.colSpan = 1; cell.rowSpan = 1;
      for (let i = 0; i < rs; i++) {
        const r = rows[p.r + i]; if (!r) break;
        for (let j = 0; j < cs; j++) {
          if (i === 0 && j === 0) continue;
          // 그 행에서 시작 열이 (p.c + j) 보다 큰 첫 셀 앞에 끼운다(없으면 맨 뒤).
          let before: HTMLTableCellElement | null = null;
          for (const cc of Array.from(r.cells)) { const q = pos.get(cc); if (q && q.c > p.c + j) { before = cc; break; } }
          const nc = newCell(undefined, cell.tagName === "TH");
          nc.setAttribute("style", cell.getAttribute("style") || CELL_STYLE);
          nc.innerHTML = "&nbsp;";
          r.insertBefore(nc, before);
        }
      }
      focusCell(cell); emit(); return;
    }
    if (op === "toggleHeader") {
      const first = rows[0]; if (!first) return;
      const toHeader = first.cells[0]?.tagName !== "TH";
      Array.from(first.cells).forEach((c) => {
        const n = document.createElement(toHeader ? "th" : "td");
        for (const a of Array.from(c.attributes)) n.setAttribute(a.name, a.value);
        const st = (c.getAttribute("style") || CELL_STYLE).replace(/;?background:#f3f4f6/g, "").replace(/;?font-weight:bold/g, "");
        n.setAttribute("style", toHeader ? `${st};background:#f3f4f6;font-weight:bold` : st);
        n.innerHTML = c.innerHTML;
        c.replaceWith(n);
      });
      emit(); return;
    }
    if (op === "widthFull" || op === "widthAuto") {
      table.style.width = op === "widthFull" ? "100%" : "";
      emit(); return;
    }
    if (op === "borderToggle") {
      const off = table.getAttribute("border") === "0";
      table.setAttribute("border", off ? "1" : "0");
      table.querySelectorAll<HTMLTableCellElement>("td,th").forEach((c) => {
        const st = c.getAttribute("style") || CELL_STYLE;
        c.setAttribute("style", off
          ? st.replace(/border:1px solid transparent/g, "border:1px solid #d1d5db")
          : st.replace(/border:1px solid #d1d5db/g, "border:1px solid transparent"));
      });
      emit(); return;
    }
    if (op === "delRow") {
      if (rows.length <= 1) table.remove();
      else row.remove();
      emit(); return;
    }
    if (op === "rowAfter" || op === "rowBefore") {
      const tr = document.createElement("tr");
      for (let i = 0; i < row.cells.length; i++) {
        const td = document.createElement("td");
        td.setAttribute("style", CELL_STYLE);
        td.innerHTML = "&nbsp;";
        tr.appendChild(td);
      }
      row.parentElement?.insertBefore(tr, op === "rowAfter" ? row.nextSibling : row);
      emit(); return;
    }
    if (op === "colAfter") {
      const { grid, pos } = buildGrid(table);
      const p = pos.get(cell);
      const cEnd = (p?.c ?? idx) + Math.max(1, cell.colSpan);   // 이 셀의 오른쪽 경계 열
      rows.forEach((r, ri) => {
        const at = grid[ri]?.[cEnd];                           // 경계 오른쪽 자리를 차지한 셀
        const left = grid[ri]?.[cEnd - 1];
        if (left && pos.get(left)?.r !== ri) return;            // 위에서 내려온 셀이 걸쳐 있으면 건너뜀
        r.insertBefore(newCell(left ?? at), at && at.parentElement === r && pos.get(at)?.r === ri ? at : null);
      });
      emit(); return;
    }
    if (op === "delCol") {
      const { grid, pos } = buildGrid(table);
      const c0 = pos.get(cell)?.c ?? idx;
      if ((grid[0]?.length ?? 0) <= 1) { table.remove(); emit(); return; }
      const seen = new Set<HTMLTableCellElement>();
      rows.forEach((_, ri) => {
        const at = grid[ri]?.[c0];
        if (!at || seen.has(at)) return;
        seen.add(at);
        if (Math.max(1, at.colSpan) > 1) at.colSpan = at.colSpan - 1;   // 병합된 셀은 한 칸만 줄인다
        else at.remove();
      });
      emit();
    }
  };

  /** 현재 셀 채우기 색(빈 값이면 지운다). */
  const cellFill = (color: string) => {
    const cell = currentCell(); if (!cell) return;
    const st = (cell.getAttribute("style") || CELL_STYLE).replace(/;?background(?:-color)?:[^;]*/g, "");
    cell.setAttribute("style", color && color !== "transparent" ? `${st};background:${color}` : st);
    emit();
  };
  /** 현재 셀 글자 정렬. */
  const cellAlign = (align: "left" | "center" | "right") => {
    const cell = currentCell(); if (!cell) return;
    const st = (cell.getAttribute("style") || CELL_STYLE).replace(/;?text-align:[^;]*/g, "");
    cell.setAttribute("style", align === "left" ? st : `${st};text-align:${align}`);
    emit();
  };

  /** 표 안에서 Tab 으로 다음 칸(Shift+Tab 은 이전 칸)으로 넘어간다.
   *  마지막 칸에서 Tab 을 누르면 워드처럼 행을 하나 더 만든다 —
   *  표를 채우다 칸이 모자라 도구 모음까지 다녀오는 일이 없도록. */
  const moveCell = (dir: 1 | -1): boolean => {
    const cell = currentCell();
    const table = cell?.closest("table");
    if (!cell || !table) return false;
    const cells = Array.from(table.querySelectorAll<HTMLTableCellElement>("td,th"));
    const i = cells.indexOf(cell);
    let next = cells[i + dir];
    if (!next && dir === 1) {
      tableOp("rowAfter");
      const after = Array.from(table.querySelectorAll<HTMLTableCellElement>("td,th"));
      next = after[i + 1];
    }
    if (!next) return false;
    const sel = window.getSelection();
    const r = document.createRange();
    r.selectNodeContents(next);      // 칸 내용을 잡아 둔다 — 바로 덮어써 넣을 수 있게
    sel?.removeAllRanges();
    sel?.addRange(r);
    return true;
  };

  // ── 그 밖의 삽입 ──────────────────────────────────────────────────────
  const insertLine = () =>
    insertHtml('<hr style="border:none;border-top:1px solid #d1d5db;margin:14px 0">');

  const insertLink = () => {
    const url = window.prompt("연결할 주소를 입력하세요", "https://");
    if (!url || url === "https://") return;
    exec("createLink", url);
  };

  const insertQuote = () =>
    insertHtml(
      '<blockquote style="margin:12px 0;padding:6px 0 6px 12px;border-left:3px solid #d1d5db;color:#6b7280">인용문</blockquote>',
    );

  const insertCode = () =>
    insertHtml(
      '<pre style="margin:10px 0;padding:10px 12px;background:#f3f4f6;border-radius:6px;' +
      'font-family:\'Courier New\',monospace;font-size:13px;white-space:pre-wrap">코드</pre>',
    );

  const insertCheck = () => insertHtml("☐&nbsp;");

  /** 이미지 크기. style 만 주면 아웃룩이 무시하는 경우가 있어 **width 속성**으로 준다.
   *  기준 폭은 600px — 메일 본문이 대개 그 정도라, 그보다 큰 원본은 잘라 맞춘다. */
  const sizeImg = (pct: number) => {
    if (!selImg) return;
    const base = Math.min(selImg.naturalWidth || 600, 600);
    selImg.setAttribute("width", String(Math.round(base * pct)));
    selImg.removeAttribute("height");
    selImg.setAttribute("style", "max-width:100%;height:auto");
    emit();
  };

  /** 주소를 입력하고 띄어쓰기/엔터를 치면 자동으로 링크가 되게 한다.
   *  (메일에서 주소가 그냥 글자로 남으면 받는 사람이 복사해서 붙여야 한다) */
  const autoLink = () => {
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed) return;
    const node = sel.anchorNode;
    if (!node || node.nodeType !== 3) return;
    const parent = (node.parentElement as HTMLElement | null);
    if (parent?.closest("a")) return;                       // 이미 링크면 그냥 둔다
    const pos = sel.anchorOffset;
    const m = (node.textContent || "").slice(0, pos).match(/(^|\s)((https?:\/\/|www\.)\S+)$/);
    if (!m) return;
    const url = m[2];
    const r = document.createRange();
    r.setStart(node, pos - url.length);
    r.setEnd(node, pos);
    const a = document.createElement("a");
    a.href = url.startsWith("http") ? url : `https://${url}`;
    a.textContent = url;
    a.setAttribute("style", "color:#2563eb");
    r.deleteContents();
    r.insertNode(a);
    const after = document.createRange();
    after.setStartAfter(a);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
  };

  const takeImages = (dt: DataTransfer | null): boolean => {
    if (!dt || !onPasteImages) return false;
    const imgs = imagesFromClipboard(dt);
    if (!imgs.length) return false;
    onPasteImages(imgs);
    return true;
  };

  const Btn = ({ title, onClick, children, wide, active }: {
    title: string; onClick: () => void; children: React.ReactNode; wide?: boolean; active?: boolean;
  }) => (
    <button type="button" title={title}
      // onMouseDown 으로 막아야 선택 영역이 풀리지 않는다.
      // (클릭 시 포커스가 옮겨가면 굵게/색상이 엉뚱한 곳에 적용된다)
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      style={{
        minWidth: wide ? undefined : 28, height: 28, padding: "0 7px", borderRadius: 6,
        cursor: "pointer", background: active ? "var(--p-tint)" : "transparent",
        border: `1px solid ${active ? "var(--p-500)" : "var(--border)"}`,
        color: active ? "var(--p-600)" : "var(--text-1)",
        fontSize: 13, lineHeight: 1, display: "inline-flex",
        alignItems: "center", justifyContent: "center", whiteSpace: "nowrap",
      }}>
      {children}
    </button>
  );

  const Pop = ({ children, width }: { children: React.ReactNode; width?: number }) => (
    <div onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "absolute", top: 32, left: 0, zIndex: 60, width,
        padding: 8, background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 8, boxShadow: "var(--shadow-md)",
      }}>
      {children}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* 도구 모음 */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center",
        padding: "6px 0 8px", borderBottom: "1px solid var(--border)",
      }}>
        <Btn title="되돌리기 (Ctrl+Z)" onClick={() => exec("undo")}>↶</Btn>
        <Btn title="다시 실행 (Ctrl+Y)" onClick={() => exec("redo")}>↷</Btn>

        <Sep />

        <select title="문단 스타일" defaultValue="<p>"
          onChange={(e) => exec("formatBlock", e.target.value)} style={selStyle}>
          {BLOCKS.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
        </select>

        <select title="글꼴" defaultValue=""
          onChange={(e) => e.target.value && exec("fontName", e.target.value)} style={selStyle}>
          {FONTS.map((f) => <option key={f.label} value={f.v}>{f.label}</option>)}
        </select>

        <select title="글자 크기" defaultValue="3"
          onChange={(e) => exec("fontSize", e.target.value)} style={selStyle}>
          {FONT_SIZES.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
        </select>

        <Sep />

        <Btn title="굵게 (Ctrl+B)" active={marks.bold} onClick={() => exec("bold")}><b>가</b></Btn>
        <Btn title="기울임 (Ctrl+I)" active={marks.italic} onClick={() => exec("italic")}><i>가</i></Btn>
        <Btn title="밑줄 (Ctrl+U)" active={marks.underline} onClick={() => exec("underline")}><u>가</u></Btn>
        <Btn title="취소선" active={marks.strikeThrough} onClick={() => exec("strikeThrough")}><s>가</s></Btn>
        <Btn title="위 첨자" onClick={() => exec("superscript")}>x²</Btn>
        <Btn title="아래 첨자" onClick={() => exec("subscript")}>x₂</Btn>

        <Sep />

        <div style={{ position: "relative" }}>
          <Btn title="글자 색" onClick={() => setMenu(menu === "color" ? null : "color")}>
            <span style={{ color: "#ef4444", fontWeight: 700 }}>가</span>
          </Btn>
          {menu === "color" && (
            <Pop width={150}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 22px)", gap: 5 }}>
                {COLORS.map((c) => (
                  <button key={c} type="button" title={c}
                    onMouseDown={(e) => { e.preventDefault(); exec("foreColor", c); setMenu(null); }}
                    style={{ width: 22, height: 22, borderRadius: 4, background: c,
                             border: "1px solid var(--border)", cursor: "pointer" }} />
                ))}
              </div>
            </Pop>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <Btn title="형광펜" onClick={() => setMenu(menu === "hilite" ? null : "hilite")}>
            <span style={{ background: "#fef08a", color: "#1f2937", padding: "0 3px", borderRadius: 3 }}>가</span>
          </Btn>
          {menu === "hilite" && (
            <Pop width={150}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 22px)", gap: 5 }}>
                {HILITES.map((c) => (
                  <button key={c} type="button" title={c === "transparent" ? "지우기" : c}
                    onMouseDown={(e) => { e.preventDefault(); exec("hiliteColor", c); setMenu(null); }}
                    style={{ width: 22, height: 22, borderRadius: 4,
                             background: c === "transparent" ? "var(--surface)" : c,
                             border: "1px solid var(--border)", cursor: "pointer",
                             fontSize: 11, color: "var(--text-3)" }}>
                    {c === "transparent" ? "✕" : ""}
                  </button>
                ))}
              </div>
            </Pop>
          )}
        </div>

        <Sep />

        {/* 자동 번호매기기 — 엔터를 치면 다음 번호가 저절로 붙는다(브라우저가 처리) */}
        <Btn title="번호 매기기" active={marks.insertOrderedList} onClick={() => exec("insertOrderedList")}>1.</Btn>
        <Btn title="글머리 기호" active={marks.insertUnorderedList} onClick={() => exec("insertUnorderedList")}>•</Btn>
        <Btn title="체크 항목" onClick={insertCheck}>☐</Btn>
        <Btn title="내어쓰기" onClick={() => exec("outdent")}>⇤</Btn>
        <Btn title="들여쓰기" onClick={() => exec("indent")}>⇥</Btn>

        <Sep />

        <Btn title="왼쪽 정렬" onClick={() => exec("justifyLeft")}>≡</Btn>
        <Btn title="가운데 정렬" active={marks.justifyCenter} onClick={() => exec("justifyCenter")}>≣</Btn>
        <Btn title="오른쪽 정렬" active={marks.justifyRight} onClick={() => exec("justifyRight")}>⋮≡</Btn>
        <Btn title="양쪽 맞춤" active={marks.justifyFull} onClick={() => exec("justifyFull")}>▤</Btn>

        <Sep />

        <Btn title="인용문" onClick={insertQuote}>❝</Btn>
        <Btn title="코드 블록" onClick={insertCode}>{"</>"}</Btn>
        <Btn title="구분선" onClick={insertLine}>―</Btn>

        <div style={{ position: "relative" }}>
          <Btn title="표" onClick={() => setMenu(menu === "table" ? null : "table")}>▦</Btn>
          {menu === "table" && !inTable && (
            <Pop width={186}>
              <TableMaker onMake={insertTable} />
              <div style={{ marginTop: 8, fontSize: 10.5, color: "var(--text-3)", lineHeight: 1.5 }}>
                표를 넣은 뒤 <b>표 안에 커서를 두고</b> 이 버튼을 다시 누르면 행·열·셀 편집 도구가 나옵니다.
                엑셀에서 복사한 셀은 붙여넣기만 해도 표로 들어옵니다.
              </div>
            </Pop>
          )}
          {menu === "table" && inTable && (
            <Pop width={300}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <ToolRow label="행">
                  <MiniBtn onClick={() => tableOp("rowBefore")}>위에 추가</MiniBtn>
                  <MiniBtn onClick={() => tableOp("rowAfter")}>아래 추가</MiniBtn>
                  <MiniBtn onClick={() => tableOp("rowUp")}>↑ 위로</MiniBtn>
                  <MiniBtn onClick={() => tableOp("rowDown")}>↓ 아래로</MiniBtn>
                  <MiniBtn onClick={() => tableOp("delRow")}>삭제</MiniBtn>
                </ToolRow>
                <ToolRow label="열">
                  <MiniBtn onClick={() => tableOp("colBefore")}>왼쪽에 추가</MiniBtn>
                  <MiniBtn onClick={() => tableOp("colAfter")}>오른쪽에 추가</MiniBtn>
                  <MiniBtn onClick={() => tableOp("delCol")}>삭제</MiniBtn>
                </ToolRow>
                <ToolRow label="셀">
                  <MiniBtn onClick={() => tableOp("mergeRight")}>→ 오른쪽과 합치기</MiniBtn>
                  <MiniBtn onClick={() => tableOp("mergeDown")}>↓ 아래와 합치기</MiniBtn>
                  <MiniBtn onClick={() => tableOp("split")}>나누기</MiniBtn>
                </ToolRow>
                <ToolRow label="정렬">
                  <MiniBtn onClick={() => cellAlign("left")}>왼쪽</MiniBtn>
                  <MiniBtn onClick={() => cellAlign("center")}>가운데</MiniBtn>
                  <MiniBtn onClick={() => cellAlign("right")}>오른쪽</MiniBtn>
                </ToolRow>
                <ToolRow label="채우기">
                  {HILITES.map((c) => (
                    <button key={c} type="button" title={c === "transparent" ? "색 없음" : c}
                      onMouseDown={(e) => { e.preventDefault(); cellFill(c); }}
                      style={{ width: 18, height: 18, borderRadius: 4, cursor: "pointer", padding: 0,
                               border: "1px solid var(--border)", background: c === "transparent" ? "transparent" : c,
                               position: "relative" }}>
                      {c === "transparent" && <span style={{ position: "absolute", inset: 0, fontSize: 11, lineHeight: "16px", color: "var(--text-3)" }}>∅</span>}
                    </button>
                  ))}
                </ToolRow>
                <ToolRow label="표">
                  <MiniBtn onClick={() => tableOp("toggleHeader")}>머리글 행</MiniBtn>
                  <MiniBtn onClick={() => tableOp("widthFull")}>폭 100%</MiniBtn>
                  <MiniBtn onClick={() => tableOp("widthAuto")}>폭 자동</MiniBtn>
                  <MiniBtn onClick={() => tableOp("borderToggle")}>테두리 켜기/끄기</MiniBtn>
                  <MiniBtn onClick={() => tableOp("delTable")}>표 삭제</MiniBtn>
                </ToolRow>
              </div>
              <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--text-3)", lineHeight: 1.5 }}>
                Tab 으로 다음 칸, 마지막 칸에서 Tab 은 행 추가. 글자 색·굵게는 위 도구 모음을 쓰세요.
              </div>
            </Pop>
          )}
        </div>

        <Btn title="링크" onClick={insertLink}>🔗</Btn>
        <Btn title="링크 해제" onClick={() => exec("unlink")}>⛓</Btn>
        {onRequestImage && <Btn title="이미지 넣기 (붙여넣기·끌어놓기도 됩니다)" onClick={onRequestImage}>🖼</Btn>}

        <div style={{ position: "relative" }}>
          <Btn title="이모지" onClick={() => setMenu(menu === "emoji" ? null : "emoji")}>🙂</Btn>
          {menu === "emoji" && (
            <Pop width={230}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 2 }}>
                {EMOJIS.map((em) => (
                  <button key={em} type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertHtml(em); setMenu(null); }}
                    style={{ height: 22, border: "none", background: "transparent",
                             cursor: "pointer", fontSize: 15, lineHeight: 1 }}>
                    {em}
                  </button>
                ))}
              </div>
            </Pop>
          )}
        </div>

        <Sep />
        <Btn title="서식 지우기" onClick={() => { exec("removeFormat"); exec("unlink"); }} wide>✕ 서식</Btn>

        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>{count}자</span>
      </div>

      {/* 이미지를 누르면 뜨는 크기 조절 막대 */}
      {selImg && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", marginTop: 6,
          background: "var(--p-tint)", border: "1px solid var(--border)", borderRadius: 8,
          fontSize: 11.5, color: "var(--text-2)",
        }}>
          <span>선택한 이미지 크기</span>
          {[0.25, 0.5, 0.75, 1].map((p) => (
            <MiniBtn key={p} onClick={() => sizeImg(p)}>{Math.round(p * 100)}%</MiniBtn>
          ))}
          <MiniBtn onClick={() => { selImg.remove(); setSelImg(null); emit(); }}>삭제</MiniBtn>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); setSelImg(null); }}
            style={{ marginLeft: "auto", border: "none", background: "transparent",
                     cursor: "pointer", color: "var(--text-3)", fontSize: 13 }}>✕</button>
        </div>
      )}

      {/* 본문 */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onKeyDown={(e) => {
          if (e.key === "Tab" && moveCell(e.shiftKey ? -1 : 1)) { e.preventDefault(); return; }
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); insertLink(); return; }
          if (e.key === " " || e.key === "Enter") autoLink();
        }}
        // 이미지를 누르면 크기 조절 막대가 뜬다(다른 곳을 누르면 닫힌다).
        onClick={(e) => {
          const t = e.target as HTMLElement;
          setSelImg(t.tagName === "IMG" ? (t as HTMLImageElement) : null);
        }}
        // 붙여넣기는 **평문으로** 받는다. 워드·웹에서 복사한 서식이 그대로 들어오면
        // 메일에서 깨지는 마크업(스타일 블록·클래스)이 섞인다.
        //
        // 단, 이미지는 예외다. 캡처나 복사한 사진을 붙여넣는 건 흔한 일인데
        // 평문만 받으면 아무 일도 일어나지 않아 "붙여넣기가 안 된다"가 된다.
        // 브라우저가 만들어 주는 data URL 을 그대로 쓰지 않고 바깥으로 넘겨
        // **서버에 올린 뒤 공개 주소로** 넣는다(버튼으로 넣을 때와 같은 길).
        // data URL 로 박으면 메일 용량이 커지고 상당수 메일 앱이 안 보여 준다.
        onPaste={(e) => {
          const text = e.clipboardData.getData("text/plain");
          const html = e.clipboardData.getData("text/html");
          // 엑셀·구글시트에서 복사한 셀은 표로 넣는다(2026-09-14 요청). 예전엔 평문으로만 받아
          // 탭으로 나뉜 글자가 됐다. 서식은 표 구조·병합·굵게·정렬·색만 남기고 버린다.
          // **표 판정이 그림보다 먼저다.** 엑셀은 셀을 복사할 때 표 HTML 과 함께 셀을 그린
          // 그림(비트맵)도 클립보드에 넣는데, 그림을 먼저 보면 표가 사진으로 들어간다(실제로 그랬다).
          const table = (html && tableFromHtml(html)) || (text && tableFromTsv(text)) || null;
          if (table) { e.preventDefault(); insertHtml(table); return; }
          if (takeImages(e.clipboardData)) { e.preventDefault(); return; }
          if (text) {
            e.preventDefault();
            document.execCommand("insertText", false, text);
            emit();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        // 떨어뜨린 파일은 바깥(작성 화면)이 한 곳에서 처리한다. 본문 안에 떨어진 그림은
        // 그 자리에 그림으로, 그 밖은 첨부로. 여기서도 받으면 같은 그림이 첨부에 한 번 더 붙었다.
        onDrop={() => setDragOver(false)}
        style={{
          flex: 1, minHeight, padding: "10px 2px", outline: "none",
          fontSize: 14, lineHeight: 1.7, color: "var(--text)", overflowY: "auto",
          borderRadius: 6,
          boxShadow: dragOver ? "inset 0 0 0 2px var(--p-500)" : "none",
        }}
      />
    </div>
  );
}

/** 표 크기를 눈으로 고른다(칸 위에 올리면 그만큼 미리 칠해진다). */
function TableMaker({ onMake }: { onMake: (r: number, c: number, header: boolean) => void }) {
  const [hover, setHover] = useState({ r: 0, c: 0 });
  const [header, setHeader] = useState(true);
  const MAX_R = 6, MAX_C = 6;
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${MAX_C}, 24px)`, gap: 2 }}
           onMouseLeave={() => setHover({ r: 0, c: 0 })}>
        {Array.from({ length: MAX_R * MAX_C }).map((_, i) => {
          const r = Math.floor(i / MAX_C) + 1, c = (i % MAX_C) + 1;
          const on = r <= hover.r && c <= hover.c;
          return (
            <button key={i} type="button"
              onMouseEnter={() => setHover({ r, c })}
              onMouseDown={(e) => { e.preventDefault(); onMake(r, c, header); }}
              style={{ width: 24, height: 18, cursor: "pointer", borderRadius: 2,
                       border: "1px solid var(--border)",
                       background: on ? "var(--p-500)" : "transparent" }} />
          );
        })}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-2)", display: "flex",
                    alignItems: "center", justifyContent: "space-between" }}>
        <span>{hover.r ? `${hover.r} × ${hover.c}` : "크기 선택"}</span>
        <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={header} onChange={(e) => setHeader(e.target.checked)} />
          머리글
        </label>
      </div>
    </div>
  );
}

function ToolRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      <span style={{ width: 36, flexShrink: 0, fontSize: 10.5, color: "var(--text-3)" }}>{label}</span>
      {children}
    </div>
  );
}

function MiniBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{ height: 24, borderRadius: 5, cursor: "pointer", fontSize: 11,
               border: "1px solid var(--border)", background: "transparent", color: "var(--text-1)" }}>
      {children}
    </button>
  );
}

const selStyle: React.CSSProperties = {
  height: 28, borderRadius: 6, border: "1px solid var(--border)",
  background: "transparent", color: "var(--text-1)", fontSize: 12, padding: "0 4px",
};

function Sep() {
  return <span style={{ width: 1, height: 18, background: "var(--border)", margin: "0 3px" }} />;
}

/**
 * 예전 본문에 쌓인 중복 구분선을 정리한다.
 *
 * 서명 구분선을 div 의 border-top 으로 그리던 시절이 있었다. 그 div 안에서 Enter 를
 * 치면 브라우저가 스타일째 복제해서, 줄을 바꿀 때마다 가로줄이 하나씩 늘었다.
 * 이미 그렇게 저장된 초안·답장을 열면 그 줄이 그대로 보이고 편집하면 또 늘어난다.
 * 그래서 불러오는 시점에 한 번 정리한다 — 선은 전부 떼고, 원래 있던 자리에
 * <hr> 하나만 세운다(<hr> 은 빈 요소라 Enter 로 복제되지 않는다).
 */
function healSeparators(root: HTMLElement): void {
  const marked = Array.from(root.querySelectorAll<HTMLDivElement>('div[style*="border-top"]'));
  if (!marked.length) return;
  marked.forEach((d) => {
    d.style.borderTop = "";
    d.style.marginTop = "";
    d.style.paddingTop = "";
  });
  const first = marked[0];
  const hr = document.createElement("hr");
  hr.setAttribute("style", "margin:12px 0;border:none;border-top:1px solid #e5e7eb");
  first.parentNode?.insertBefore(hr, first);
}
